import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as appApi from '../api/appApi';
import { BackgroundTasksPanel } from '../components/BackgroundTasksPanel';
import { uploadOrderFile } from '../components/FileUpload';
import { formatApiError } from './queryMeta';
import { queryClient } from './queryClient';
import { queryKeys } from '../hooks/queryKeys';
import type { Order } from '../types';
import { uid } from '../utils/orderHelpers';

export type BackgroundTaskStatus = 'pending' | 'running' | 'success' | 'error';

export type DraftSavePayload = {
  orderData: Omit<Order, 'id' | 'orderId' | 'createdAt' | 'updatedAt' | 'stageHistory'>;
  orderFiles: File[];
  lineItemFiles: { lineItemId: string; files: File[] }[];
};

export type DraftSaveTask = {
  id: string;
  kind: 'draft_save';
  label: string;
  status: BackgroundTaskStatus;
  progress: number;
  progressLabel: string;
  error?: string;
  orderId?: string;
  completedUploadKeys?: string[];
  createdAt: number;
  payload: DraftSavePayload;
};

export type BackgroundTask = DraftSaveTask;

type BackgroundTasksContextValue = {
  tasks: BackgroundTask[];
  enqueueDraftSave: (label: string, payload: DraftSavePayload) => string;
  retryTask: (taskId: string) => void;
  dismissTask: (taskId: string) => void;
  clearCompleted: () => void;
};

const BackgroundTasksContext = createContext<BackgroundTasksContextValue | null>(null);

export function draftUploadKey(file: File, lineItemId?: string): string {
  return `${lineItemId ?? 'order'}:${file.name}:${file.size}:${file.lastModified}`;
}

function listDraftUploads(payload: DraftSavePayload): { lineItemId?: string; file: File }[] {
  return [
    ...payload.orderFiles.map((file) => ({ file })),
    ...payload.lineItemFiles.flatMap(({ lineItemId, files }) =>
      files.map((file) => ({ lineItemId, file })),
    ),
  ];
}

function invalidateOrder(orderId: string) {
  void queryClient.invalidateQueries({ queryKey: ['app'] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.orderActivity(orderId) });
}

async function runDraftSave(
  taskId: string,
  task: DraftSaveTask,
  patch: (id: string, update: Partial<DraftSaveTask>) => void,
): Promise<void> {
  const completed = new Set(task.completedUploadKeys ?? []);
  const pendingUploads = listDraftUploads(task.payload).filter(
    ({ file, lineItemId }) => !completed.has(draftUploadKey(file, lineItemId)),
  );

  patch(taskId, {
    status: 'running',
    progress: task.orderId ? Math.max(20, Math.round((completed.size / Math.max(listDraftUploads(task.payload).length, 1)) * 100)) : 8,
    progressLabel: task.orderId
      ? pendingUploads.length > 0
        ? 'Resuming upload…'
        : 'Finishing…'
      : 'Creating draft…',
    error: undefined,
  });

  let orderId = task.orderId;

  try {
    if (!orderId) {
      const order = await appApi.createOrder(task.payload.orderData);
      orderId = order.id;
      patch(taskId, { orderId: order.id, progress: 18, progressLabel: 'Uploading files…' });
    }

    const totalUploads = listDraftUploads(task.payload).length;

    if (totalUploads === 0 || pendingUploads.length === 0) {
      invalidateOrder(orderId);
      patch(taskId, {
        status: 'success',
        progress: 100,
        progressLabel: 'Draft saved',
        orderId,
        completedUploadKeys: [...completed],
      });
      return;
    }

    for (const { file, lineItemId } of pendingUploads) {
      const doneBefore = completed.size;
      patch(taskId, {
        progress: 18 + Math.round((doneBefore / totalUploads) * 78),
        progressLabel: `Uploading ${file.name}…`,
        orderId,
      });
      await uploadOrderFile(orderId, file, 'attachment', lineItemId ? { lineItemId } : undefined);
      completed.add(draftUploadKey(file, lineItemId));
      patch(taskId, { completedUploadKeys: [...completed], orderId });
    }

    invalidateOrder(orderId);
    patch(taskId, {
      status: 'success',
      progress: 100,
      progressLabel: 'Draft saved',
      orderId,
      completedUploadKeys: [...completed],
    });
  } catch (err) {
    patch(taskId, {
      status: 'error',
      progress: Math.round((completed.size / Math.max(listDraftUploads(task.payload).length, 1)) * 100),
      progressLabel: 'Failed',
      error: formatApiError(err),
      orderId,
      completedUploadKeys: [...completed],
    });
  }
}

export function BackgroundTasksProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  const tasksRef = useRef<BackgroundTask[]>([]);
  const runningRef = useRef<Set<string>>(new Set());

  tasksRef.current = tasks;

  const patchTask = useCallback((id: string, update: Partial<DraftSaveTask>) => {
    setTasks((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, ...update } : t));
      tasksRef.current = next;
      return next;
    });
  }, []);

  const startTaskById = useCallback(
    (taskId: string) => {
      if (runningRef.current.has(taskId)) return;
      const task = tasksRef.current.find((t) => t.id === taskId);
      if (!task || task.kind !== 'draft_save') return;
      runningRef.current.add(taskId);
      void runDraftSave(taskId, task, patchTask).finally(() => {
        runningRef.current.delete(taskId);
      });
    },
    [patchTask],
  );

  const enqueueDraftSave = useCallback(
    (label: string, payload: DraftSavePayload) => {
      const id = uid();
      const task: DraftSaveTask = {
        id,
        kind: 'draft_save',
        label,
        status: 'pending',
        progress: 0,
        progressLabel: 'Queued…',
        createdAt: Date.now(),
        payload,
        completedUploadKeys: [],
      };
      setTasks((prev) => {
        const next = [task, ...prev].slice(0, 20);
        tasksRef.current = next;
        return next;
      });
      queueMicrotask(() => startTaskById(id));
      return id;
    },
    [startTaskById],
  );

  const retryTask = useCallback(
    (taskId: string) => {
      setTasks((prev) => {
        const task = prev.find((t) => t.id === taskId);
        if (!task || task.kind !== 'draft_save') return prev;
        const next = prev.map((t) =>
          t.id === taskId
            ? {
                ...t,
                status: 'pending' as const,
                progressLabel: 'Retrying…',
                error: undefined,
              }
            : t,
        );
        tasksRef.current = next;
        return next;
      });
      queueMicrotask(() => startTaskById(taskId));
    },
    [startTaskById],
  );

  const dismissTask = useCallback((taskId: string) => {
    setTasks((prev) => {
      const next = prev.filter((t) => t.id !== taskId);
      tasksRef.current = next;
      return next;
    });
  }, []);

  const clearCompleted = useCallback(() => {
    setTasks((prev) => {
      const next = prev.filter((t) => t.status !== 'success');
      tasksRef.current = next;
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ tasks, enqueueDraftSave, retryTask, dismissTask, clearCompleted }),
    [tasks, enqueueDraftSave, retryTask, dismissTask, clearCompleted],
  );

  return (
    <BackgroundTasksContext.Provider value={value}>
      {children}
      <BackgroundTasksPanel />
    </BackgroundTasksContext.Provider>
  );
}

export function useBackgroundTasks() {
  const ctx = useContext(BackgroundTasksContext);
  if (!ctx) {
    throw new Error('useBackgroundTasks must be used within BackgroundTasksProvider');
  }
  return ctx;
}
